import { onBeforeUnmount, watch } from 'vue';
import type { Ref } from 'vue';

/**
 * What a drawer and a modal both owe.
 *
 * Shipping no component library means these are ours to get right, and each
 * one is a way an overlay stops being usable by keyboard: focus left behind
 * the overlay, focus lost on close, Tab escaping to the page underneath,
 * Escape doing nothing, the page scrolling behind. Written once here with a
 * test each, rather than hoped for in every module.
 */

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Deliberately not `offsetParent`, which is the usual shortcut and wrong twice
 * over: it is `null` for any `position: fixed` element — which is what an
 * overlay panel is — and `null` for everything where nothing has been laid
 * out, which silently collapses the cycle to a single element.
 *
 * Explicit hiding is what actually needs excluding, and `v-show` is the common
 * case: it leaves the element in the DOM with an inline `display: none`.
 */
function isHidden(element: HTMLElement): boolean {
	// `hidden` is `boolean | 'until-found'`, and until-found is still not
	// focusable, so anything truthy counts.
	return (
		Boolean(element.hidden) ||
		element.style.display === 'none' ||
		element.getAttribute('aria-hidden') === 'true'
	);
}

export function focusableWithin(container: HTMLElement): HTMLElement[] {
	return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
		(element) => !isHidden(element),
	);
}

/**
 * Nested overlays are a real case — a confirm modal opened from inside an edit
 * drawer — so the lock is counted rather than set and cleared. The inner one
 * closing must not hand scrolling back to a page still covered by the outer.
 */
let lockCount = 0;
let restoreOverflow = '';

function lockScroll(): void {
	if (lockCount === 0) {
		restoreOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
	}
	lockCount += 1;
}

function unlockScroll(): void {
	lockCount = Math.max(0, lockCount - 1);
	if (lockCount === 0) document.body.style.overflow = restoreOverflow;
}

/** Tests only: module state must not leak between them. */
export function resetScrollLock(): void {
	lockCount = 0;
	restoreOverflow = '';
	document.body.style.overflow = '';
}

export interface OverlayOptions {
	readonly open: () => boolean;
	readonly container: Ref<HTMLElement | undefined>;
	readonly onClose: () => void;
	/**
	 * A modal refuses it. The asymmetry is the point: a stray click should not
	 * answer a question about deleting something.
	 */
	readonly closeOnBackdrop?: boolean;
}

export interface Overlay {
	readonly onBackdropClick: (event: MouseEvent) => void;
}

export function useOverlay(options: OverlayOptions): Overlay {
	let previouslyFocused: HTMLElement | undefined;

	function onKeydown(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			event.stopPropagation();
			options.onClose();
			return;
		}
		if (event.key !== 'Tab') return;

		const container = options.container.value;
		if (container === undefined) return;

		const focusable = focusableWithin(container);
		if (focusable.length === 0) {
			// Nothing to cycle between, so the only correct answer is to keep
			// focus here rather than let Tab walk onto the page behind.
			event.preventDefault();
			return;
		}

		const first = focusable[0] as HTMLElement;
		const last = focusable[focusable.length - 1] as HTMLElement;
		const active = document.activeElement;

		if (event.shiftKey && (active === first || !container.contains(active))) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && (active === last || !container.contains(active))) {
			event.preventDefault();
			first.focus();
		}
	}

	function activate(): void {
		previouslyFocused = (document.activeElement as HTMLElement | null) ?? undefined;
		lockScroll();
		document.addEventListener('keydown', onKeydown, true);

		// After the overlay has rendered, or there is nothing to focus yet.
		queueMicrotask(() => {
			const container = options.container.value;
			if (container === undefined) return;
			(focusableWithin(container)[0] ?? container).focus();
		});
	}

	function deactivate(): void {
		document.removeEventListener('keydown', onKeydown, true);
		unlockScroll();
		// Back where they were, so closing does not dump focus on the body and
		// leave a keyboard user at the top of the document.
		previouslyFocused?.focus();
		previouslyFocused = undefined;
	}

	let active = false;

	watch(
		options.open,
		(isOpen) => {
			if (isOpen && !active) {
				active = true;
				activate();
			} else if (!isOpen && active) {
				active = false;
				deactivate();
			}
		},
		{ immediate: true },
	);

	// An overlay unmounted while open — a route change behind it — must not
	// leave the page locked and the listener attached.
	onBeforeUnmount(() => {
		if (active) {
			active = false;
			deactivate();
		}
	});

	return {
		onBackdropClick: (event: MouseEvent) => {
			if (options.closeOnBackdrop !== true) return;
			// Only the backdrop itself, never a click that bubbled out of the
			// panel sitting on top of it.
			if (event.target === event.currentTarget) options.onClose();
		},
	};
}
